'use client'

import {
    type AnchorHTMLAttributes,
    type ComponentType,
    type KeyboardEvent,
    type MouseEvent,
    type ReactNode,
    type RefAttributes,
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState
} from 'react'
import {cn} from './cn'
import {smoothScrollBehavior} from './smoothScrollBehavior'

export interface SegmentedTabItem {
    readonly key: string
    readonly label: ReactNode
    /** Renders the segment as a link; otherwise it is a tab button reported through `onChange`. */
    readonly href?: string
    readonly icon?: ComponentType<{ className?: string }>
    readonly badge?: string | number
    readonly disabled?: boolean
    readonly ariaLabel?: string
}

export interface SegmentedTabsProps {
    readonly items: readonly SegmentedTabItem[]
    readonly activeKey: string
    readonly onChange?: (key: string) => void
    /** Names the tablist of button segments. Link segments are named by the surrounding `<nav>`. */
    readonly ariaLabel?: string
    readonly scrollLeftLabel: string
    readonly scrollRightLabel: string
    /** Stretch the track to the available width with equal segments. */
    readonly fill?: boolean
    /** Tighter segment padding, for tabs sharing a row with a heading on narrow screens. */
    readonly compact?: boolean
    readonly className?: string
    /** Remembers the indicator between remounts, so it slides from the previous route's segment. */
    readonly indicatorMemoryKey?: string
    /** Links the selected tab to its rendered panel, whose id is `${panelIdPrefix}${key}`. */
    readonly panelIdPrefix?: string
}

type SegmentLinkProps = AnchorHTMLAttributes<HTMLAnchorElement> & RefAttributes<HTMLAnchorElement> & {
    href: string
    scroll?: boolean
}

type IndicatorBox = { left: number; width: number }

const indicatorMemory = new Map<string, IndicatorBox>()

const OVERFLOW_FADE_PX = 44
const EDGE_TOLERANCE_PX = 1

const getFadeMask = (fadeLeft: boolean, fadeRight: boolean): string | undefined => {
    if (!fadeLeft && !fadeRight) return undefined

    const start = fadeLeft ? `transparent 0, black ${OVERFLOW_FADE_PX}px` : 'black 0'
    const end = fadeRight ? `black calc(100% - ${OVERFLOW_FADE_PX}px), transparent 100%` : 'black 100%'

    return `linear-gradient(to right, ${start}, ${end})`
}

const isPlainPrimaryClick = (event: MouseEvent) =>
    event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey

const ChevronIcon = ({direction}: { direction: 'left' | 'right' }) => (
    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2.25}
            d={direction === 'left' ? 'M15 19l-7-7 7-7' : 'M9 5l7 7-7 7'}
        />
    </svg>
)

/**
 * The default control for switching between sibling views or subpages.
 * Colors follow `--segmented-tabs-track` and `--segmented-tabs-accent` when a surface sets them,
 * otherwise the app's primary palette.
 */
function SegmentedTabs({
    items,
    activeKey,
    onChange,
    ariaLabel,
    scrollLeftLabel,
    scrollRightLabel,
    fill = true,
    compact = false,
    className,
    indicatorMemoryKey,
    panelIdPrefix,
    RouterLink
}: SegmentedTabsProps & {RouterLink: ComponentType<SegmentLinkProps>}) {
    const scrollerRef = useRef<HTMLDivElement>(null)
    const listRef = useRef<HTMLDivElement>(null)
    const segmentRefs = useRef(new Map<string, HTMLElement>())
    const restoringIndicator = useRef(false)

    // A clicked segment slides right away instead of waiting for the route to report it active.
    const [pendingKey, setPendingKey] = useState<string | null>(null)
    const shownKey = pendingKey ?? activeKey

    const [indicator, setIndicator] = useState<IndicatorBox | null>(() => {
        const remembered = indicatorMemoryKey ? indicatorMemory.get(indicatorMemoryKey) ?? null : null
        restoringIndicator.current = remembered !== null
        return remembered
    })
    const [indicatorAnimated, setIndicatorAnimated] = useState(false)
    const [overflow, setOverflow] = useState({left: false, right: false})

    const isTablist = items.some(item => !item.href)
    const layoutSignature = items.map(item => `${item.key}:${String(item.label)}:${item.badge ?? ''}`).join('|')

    useEffect(() => setPendingKey(null), [activeKey])

    const measureIndicator = useCallback(() => {
        const segment = segmentRefs.current.get(shownKey)
        if (!segment) {
            setIndicator(null)
            return
        }

        const next = {left: segment.offsetLeft, width: segment.offsetWidth}
        if (indicatorMemoryKey) indicatorMemory.set(indicatorMemoryKey, next)
        setIndicator(current => current?.left === next.left && current.width === next.width ? current : next)
    }, [indicatorMemoryKey, shownKey])

    const measureOverflow = useCallback(() => {
        const scroller = scrollerRef.current
        if (!scroller) return

        const {scrollLeft, clientWidth} = scroller
        // The animated indicator can temporarily extend scrollWidth beyond the actual tabs.
        const contentWidth = listRef.current?.offsetWidth ?? scroller.scrollWidth
        const canScroll = contentWidth > clientWidth + EDGE_TOLERANCE_PX
        const left = canScroll && scrollLeft > EDGE_TOLERANCE_PX
        const right = canScroll && scrollLeft + clientWidth < contentWidth - EDGE_TOLERANCE_PX
        setOverflow(current => current.left === left && current.right === right ? current : {left, right})
    }, [])

    useLayoutEffect(() => {
        if (restoringIndicator.current) {
            // Paint the remembered position first, then slide to the current segment.
            const frame = window.requestAnimationFrame(() => {
                restoringIndicator.current = false
                setIndicatorAnimated(true)
            })
            return () => window.cancelAnimationFrame(frame)
        }

        measureIndicator()
        measureOverflow()

        const list = listRef.current
        const scroller = scrollerRef.current
        if (!list || !scroller || typeof ResizeObserver === 'undefined') return

        const resizeObserver = new ResizeObserver(() => {
            measureIndicator()
            measureOverflow()
        })
        resizeObserver.observe(list)
        resizeObserver.observe(scroller)

        return () => resizeObserver.disconnect()
    }, [indicatorAnimated, layoutSignature, measureIndicator, measureOverflow])

    useEffect(() => {
        if (!indicator || indicatorAnimated || restoringIndicator.current) return

        // The first placement snaps; later changes slide.
        const frame = window.requestAnimationFrame(() => setIndicatorAnimated(true))
        return () => window.cancelAnimationFrame(frame)
    }, [indicator, indicatorAnimated])

    const hasScrolledIntoView = useRef(false)
    useEffect(() => {
        const scroller = scrollerRef.current
        const segment = segmentRefs.current.get(shownKey)
        if (!scroller || !segment) return

        const visibleStart = scroller.scrollLeft + OVERFLOW_FADE_PX
        const visibleEnd = scroller.scrollLeft + scroller.clientWidth - OVERFLOW_FADE_PX
        const segmentEnd = segment.offsetLeft + segment.offsetWidth
        const isClipped = segment.offsetLeft < visibleStart || segmentEnd > visibleEnd

        if (isClipped) {
            const centered = segment.offsetLeft + segment.offsetWidth / 2 - scroller.clientWidth / 2
            const contentWidth = listRef.current?.offsetWidth ?? scroller.scrollWidth
            const left = Math.max(0, Math.min(centered, contentWidth - scroller.clientWidth))
            if (typeof scroller.scrollTo === 'function') {
                scroller.scrollTo({left, behavior: hasScrolledIntoView.current ? smoothScrollBehavior() : 'auto'})
            } else {
                scroller.scrollLeft = left
            }
        }
        hasScrolledIntoView.current = true
    }, [layoutSignature, shownKey])

    const scrollByPage = (direction: -1 | 1) => {
        const scroller = scrollerRef.current
        if (!scroller) return

        const step = Math.max(scroller.clientWidth * 0.6, 96)
        scroller.scrollBy({left: direction * step, behavior: smoothScrollBehavior()})
    }

    const enabledItems = items.filter(item => !item.disabled)

    const handleTabKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
        const currentIndex = enabledItems.findIndex(item => item.key === shownKey)
        const lastIndex = enabledItems.length - 1
        const nextIndex = {
            ArrowLeft: currentIndex > 0 ? currentIndex - 1 : lastIndex,
            ArrowRight: currentIndex < lastIndex ? currentIndex + 1 : 0,
            Home: 0,
            End: lastIndex
        }[event.key]
        if (nextIndex === undefined) return

        event.preventDefault()
        const next = enabledItems[nextIndex]
        if (!next) return

        onChange?.(next.key)
        segmentRefs.current.get(next.key)?.focus()
    }

    const showControls = overflow.left || overflow.right
    const fadeMask = getFadeMask(overflow.left, overflow.right)
    // min-h/w-0 overrides the Customer App's page-wide button minimum, so both apps render the same 32px control.
    const controlClasses = cn(
        'absolute top-1/2 z-10 inline-flex h-8 min-h-0 w-8 min-w-0 -translate-y-1/2 items-center justify-center rounded-full',
        'bg-white text-[var(--segmented-tabs-accent,var(--color-primary-600))] shadow-[0_2px_8px_rgba(40,72,150,0.18)] dark:bg-gray-700 dark:text-primary-300',
        'transition-[opacity,scale] duration-press ease-out active:scale-[0.97] motion-reduce:transition-opacity motion-reduce:active:scale-100',
        'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-[var(--segmented-tabs-accent,var(--color-primary-600))]',
        'disabled:pointer-events-none disabled:opacity-0'
    )

    return (
        <div
            className={cn(
                'relative min-w-0 rounded-full bg-[var(--segmented-tabs-track,var(--color-primary-50))] p-1 dark:bg-gray-800',
                fill ? 'w-full' : 'w-fit max-w-full',
                className
            )}
            data-segmented-tabs
        >
            <div
                ref={scrollerRef}
                onScroll={measureOverflow}
                className="overflow-x-auto rounded-full [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                style={fadeMask ? {maskImage: fadeMask, WebkitMaskImage: fadeMask} : undefined}
                data-segmented-tabs-scroller
            >
                <div
                    ref={listRef}
                    role={isTablist ? 'tablist' : undefined}
                    aria-label={isTablist ? ariaLabel : undefined}
                    className={cn(
                        'relative flex w-max gap-1',
                        fill && 'min-w-full'
                    )}
                >
                    {indicator && (
                        <span
                            aria-hidden="true"
                            data-segmented-tabs-indicator
                            className={cn(
                                'absolute inset-y-0 left-0 rounded-full bg-white shadow-[0_2px_8px_rgba(40,72,150,0.14)] dark:bg-gray-700',
                                indicatorAnimated && 'transition-[transform,width] duration-normal ease-[cubic-bezier(0.23,1,0.32,1)] motion-reduce:transition-none'
                            )}
                            style={{width: indicator.width, transform: `translateX(${indicator.left}px)`}}
                        />
                    )}
                    {items.map(item => {
                        const selected = item.key === shownKey
                        const Icon = item.icon
                        const segmentClasses = cn(
                            // Filled segments share the width equally, yet each keeps its own content width when space runs short.
                            fill ? 'min-w-max flex-1 basis-0' : 'shrink-0',
                            'relative flex min-h-10 items-center justify-center gap-1.5 whitespace-nowrap rounded-full text-sm font-medium leading-none',
                            compact ? 'px-3' : 'px-4',
                            'transition-colors duration-normal focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--segmented-tabs-accent,var(--color-primary-600))]',
                            selected && !indicator && 'bg-white shadow-sm dark:bg-gray-700',
                            item.disabled
                                ? 'cursor-not-allowed text-gray-300 dark:text-gray-600'
                                : selected
                                    ? 'text-[var(--segmented-tabs-accent,var(--color-primary-600))] dark:text-primary-300'
                                    : 'text-gray-500 hover:text-[var(--segmented-tabs-accent,var(--color-primary-600))] dark:text-gray-400 dark:hover:text-primary-300'
                        )
                        const content = (
                            <>
                                {Icon && <Icon className="h-4 w-4 shrink-0"/>}
                                <span>{item.label}</span>
                                {item.badge !== undefined && (
                                    <span
                                        aria-hidden="true"
                                        className="inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-primary-100 px-1.5 text-xs font-semibold text-primary-700 dark:bg-primary-900/40 dark:text-primary-200"
                                    >
                                        {item.badge}
                                    </span>
                                )}
                            </>
                        )
                        const setSegmentRef = (element: HTMLElement | null) => {
                            if (element) segmentRefs.current.set(item.key, element)
                            else segmentRefs.current.delete(item.key)
                        }
                        const accessibleName = item.ariaLabel
                            ?? (item.badge !== undefined && typeof item.label === 'string' ? `${item.label} (${item.badge})` : undefined)

                        if (item.href && !item.disabled) {
                            return (
                                <RouterLink
                                    key={item.key}
                                    ref={setSegmentRef}
                                    href={item.href}
                                    scroll={false}
                                    aria-current={item.key === activeKey ? 'page' : undefined}
                                    aria-label={accessibleName}
                                    className={segmentClasses}
                                    onClick={event => {
                                        if (!isPlainPrimaryClick(event)) return
                                        setPendingKey(item.key)
                                        if (item.key !== activeKey) onChange?.(item.key)
                                    }}
                                >
                                    {content}
                                </RouterLink>
                            )
                        }

                        return (
                            <button
                                key={item.key}
                                ref={setSegmentRef}
                                type="button"
                                role={isTablist ? 'tab' : undefined}
                                aria-selected={isTablist ? selected : undefined}
                                aria-controls={isTablist && selected && panelIdPrefix ? `${panelIdPrefix}${item.key}` : undefined}
                                aria-label={accessibleName}
                                tabIndex={isTablist && !selected ? -1 : undefined}
                                disabled={item.disabled}
                                className={segmentClasses}
                                onClick={() => {
                                    if (item.key === activeKey) return
                                    setPendingKey(item.key)
                                    onChange?.(item.key)
                                }}
                                onKeyDown={isTablist ? handleTabKeyDown : undefined}
                            >
                                {content}
                            </button>
                        )
                    })}
                </div>
            </div>
            {showControls && (
                <>
                    <button
                        type="button"
                        aria-label={scrollLeftLabel}
                        disabled={!overflow.left}
                        onClick={() => scrollByPage(-1)}
                        className={cn(controlClasses, 'left-1.5')}
                        data-segmented-tabs-scroll-left
                    >
                        <ChevronIcon direction="left"/>
                    </button>
                    <button
                        type="button"
                        aria-label={scrollRightLabel}
                        disabled={!overflow.right}
                        onClick={() => scrollByPage(1)}
                        className={cn(controlClasses, 'right-1.5')}
                        data-segmented-tabs-scroll-right
                    >
                        <ChevronIcon direction="right"/>
                    </button>
                </>
            )}
        </div>
    )
}

// Shared UI stays framework-free, so each app package builds its tabs from its router link:
// `const SegmentedTabs = createSegmentedTabs(Link)` with next/link.
export function createSegmentedTabs(RouterLink: ComponentType<SegmentLinkProps>) {
    function RoutedSegmentedTabs(props: SegmentedTabsProps) {
        return <SegmentedTabs {...props} RouterLink={RouterLink}/>
    }

    return RoutedSegmentedTabs
}
